import { useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  IconPhoto,
  IconPhotoUp,
  IconSwitchHorizontal,
} from "@tabler/icons-react";
import { SourceImageDto } from "@repo/api/source-image";
import { Button } from "@/lib/components/Button/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { SourceImageReplaceDialog } from "../../SourceImageSelectorDialog";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import Image from "@/lib/components/Image";
import ImageBadge from "@/lib/components/ImageBadge";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import Toast from "@/lib/components/Toast";
import { cn } from "@/lib/utils";

type Props = {
  startImage: SourceImageDto | null;
  endImage: SourceImageDto | null;
  onPick: (startImage: SourceImageDto | null, endImage: SourceImageDto) => void;
};

export default function SpeedRampEndImageStep({
  startImage,
  endImage,
  onPick,
}: Props) {
  const [isReplaceDialogOpen, setIsReplaceDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [localStartImage, setLocalStartImage] = useState<SourceImageDto | null>(
    startImage,
  );
  const [selectedImage, setSelectedImage] = useState<SourceImageDto | null>(
    endImage,
  );

  const uploadSourceImages = useEditorStore(
    (state) => state.service.uploadSourceImages,
  );

  const handleSelect = (sourceImage: SourceImageDto) => {
    setIsReplaceDialogOpen(false);
    setSelectedImage(sourceImage);
  };

  const handleSwap = () => {
    setLocalStartImage(selectedImage);
    setSelectedImage(localStartImage);
  };

  const handleDrop = async (files: File[]) => {
    const file = files[0];
    if (!file || isUploading) return;

    setIsUploading(true);
    try {
      const [uploaded] = await uploadSourceImages([file]);
      if (uploaded) {
        setSelectedImage(uploaded);
      }
    } catch {
      Toast.error("Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/webp": [".webp"],
    },
    multiple: false,
    noClick: true,
    disabled: isUploading || isReplaceDialogOpen,
    onDrop: handleDrop,
  });

  return (
    <div className="flex h-full w-full flex-1 flex-col self-stretch px-5 pt-5 pb-4">
      <SourceImageReplaceDialog
        isOpen={isReplaceDialogOpen}
        setIsOpen={setIsReplaceDialogOpen}
        handleSelect={handleSelect}
      />
      <div className="flex min-h-0 flex-1 gap-4">
        <div className="relative flex w-1/2 items-center justify-center overflow-hidden rounded-xl bg-grey-150">
          {localStartImage && (
            <>
              <Image
                src={localStartImage.src}
                alt="Start image"
                className="h-full w-full object-contain"
              />
              <ImageBadge className="right-3 bottom-2.5">
                Start image
              </ImageBadge>
            </>
          )}
        </div>

        <div className="flex flex-col items-center justify-center gap-3">
          {localStartImage && (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="inline-block w-fit">
                  <Button
                    variant="ghost"
                    size="iconSm"
                    aria-label="Swap start and end image"
                    onClick={handleSwap}
                    disabled={!selectedImage || isUploading}
                    className={cn(
                      "[&_svg]:size-5",
                      selectedImage &&
                        !isUploading &&
                        "[&_svg]:stroke-[url(#icon-gradient)]",
                    )}
                  >
                    <IconSwitchHorizontal />
                  </Button>
                </span>
              </TooltipTrigger>
              {(!selectedImage || isUploading) && (
                <TooltipContent side="bottom">
                  Add an end image to enable swapping.
                </TooltipContent>
              )}
            </Tooltip>
          )}
        </div>

        <div
          {...getRootProps()}
          className={cn(
            "relative flex w-1/2 flex-col items-center justify-center overflow-hidden rounded-xl bg-grey-150",
            !selectedImage && "p-4",
          )}
        >
          <input {...getInputProps()} />

          {isUploading ? (
            <LoadingSpinner />
          ) : selectedImage ? (
            <>
              <Image
                src={selectedImage.src}
                alt="End image"
                className="h-full w-full object-contain"
              />
              <ImageBadge className="right-3 bottom-2.5">End image</ImageBadge>
            </>
          ) : (
            <>
              <p className="text-center font-semibold text-text-primary">
                End image
              </p>
              <p className="mt-2 max-w-80 text-center text-sm text-text-secondary">
                This image will be used as the last frame of the animation.
              </p>
              <Button
                variant="outline"
                className="mt-6"
                size="sm"
                iconPosition="leading"
                icon={<IconPhotoUp />}
                onClick={() => setIsReplaceDialogOpen(true)}
              >
                Add end image
              </Button>
              <p className="mt-3 text-xs text-text-secondary">
                or drag and drop an image here
              </p>
            </>
          )}

          {isDragActive && (
            <div className="absolute inset-0 z-40 flex flex-col items-center justify-center rounded-xl border border-dashed border-primary-400 bg-primary-100">
              <IconPhotoUp stroke={1.5} size={32} color="url(#icon-gradient)" />
              <div className="h-4" />
              <h1 className="text-base font-semibold">Drop image</h1>
              <p className="text-sm text-text-secondary">
                to set the end image
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-3">
        {selectedImage && !isUploading && (
          <Button
            variant="outline"
            size="sm"
            iconPosition="leading"
            icon={<IconPhoto />}
            onClick={() => setIsReplaceDialogOpen(true)}
          >
            Replace end image
          </Button>
        )}
        <Button
          size="sm"
          disabled={!selectedImage || isUploading}
          onClick={() => {
            if (!selectedImage) return;
            onPick(localStartImage, selectedImage);
          }}
        >
          Done
        </Button>
      </div>
    </div>
  );
}
